// HRDashboard.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Collapse,
  alpha,
  useTheme,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  config,
  getUserData,
  requestID,
  baseUrl,
} from "../../../../common/utils";
import axios from "axios";
import { allProjectsByHr, getAllAssignments, getAllEmployees } from "../../../../api/BusinessOwner";
import { IonContent, IonPage } from "@ionic/react";
import {
  ExpandMore,
  ExpandLess,
  Delete,
  Refresh,
  People,
  Assignment,
  BusinessCenter,
  Group,
  Clear,
} from "@mui/icons-material";

const assignStaffToProject = async ({
  projectId,
  staffId,
}: {
  projectId: string;
  staffId: string;
}) => {
  const response = await axios.post(
    `${baseUrl()}/services/assignStaffToProject`,
    {
      requestID: requestID(),
      selectedStaff: staffId,
      project_id: projectId,
    },
    config
  );
  return response.data;
};

const disassignStaffFromProject = async (assignmentId: string) => {
  console.log("disassigning assignment id", assignmentId);
  
  const response = await axios.post(
    `${baseUrl()}/services/deleteAssignments`,  
    {
      requestID: requestID(),
      assignmentId: assignmentId,
    },
    config
  );
  console.log("disassign response", response);
};

// Group assignments by project
const groupAssignmentsByProject = (assignments: any[]) => {
  if (!assignments) return {};
  
  return assignments.reduce((groups, assignment) => {
    const projectId = assignment.project_id;
    if (!groups[projectId]) {
      groups[projectId] = {
        project_name: assignment.project_name,
        project_id: assignment.project_id,
        assignments: []
      };
    }
    groups[projectId].assignments.push(assignment);
    return groups;
  }, {});
};

// Get unique staff count across all projects
const getUniqueStaffCount = (assignments: any[]) => {
  if (!assignments) return 0;
  const uniqueStaffIds = new Set(assignments.map(assignment => assignment.user_id));
  return uniqueStaffIds.size;
};

const HRDashboard: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [disassignDialogOpen, setDisassignDialogOpen] = useState(false);
  const [assignmentToDisassign, setAssignmentToDisassign] = useState<any>(null);
  const [expandedProjects, setExpandedProjects] = useState<{[key: string]: boolean}>({});
  const [projectFilter, setProjectFilter] = useState("");

  const { data: projects, isLoading: loadingProjects, refetch: refetchProjects } = useQuery({
    queryKey: ["projects"],
    queryFn: allProjectsByHr,
    select: (res) => res.response,
  });

  const { data: employees, isLoading: loadingStaff, refetch: refetchEmployees } = useQuery({
    queryKey: ["employees"],
    queryFn: getAllEmployees,
  });

  const { data: assignments, isLoading: loadingAssignments, refetch: refetchAssignments } = useQuery({
    queryKey: ["assignments"],
    queryFn: getAllAssignments,
  });

  const assignmentMutation = useMutation({
    mutationFn: assignStaffToProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
      setSelectedProjectId("");
      setSelectedStaffId("");
    },
  });

  const disassignMutation = useMutation({
    mutationFn: disassignStaffFromProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
      setDisassignDialogOpen(false);
      setAssignmentToDisassign(null);
    },
  });

  // Filter assignments based on project filter
  const filteredAssignments = useMemo(() => {
    if (!assignments) return [];
    if (!projectFilter) return assignments;
    return assignments.filter(assignment => assignment.project_id === projectFilter);
  }, [assignments, projectFilter]);

  // Compute statistics based on ALL assignments (not filtered)
  const stats = useMemo(() => {
    const totalProjects = projects?.length || 0;
    const totalAssignments = assignments?.length || 0;
    const totalUniqueStaff = getUniqueStaffCount(assignments || []);
    
    return { totalProjects, totalAssignments, totalUniqueStaff };
  }, [projects, assignments]);

  const groupedAssignments = groupAssignmentsByProject(filteredAssignments || []);

  // Automatically expand the project when filter is applied
  useEffect(() => {
    if (projectFilter) {
      setExpandedProjects(prev => ({
        ...prev,
        [projectFilter]: true
      }));
    }
  }, [projectFilter]);

  const handleRefresh = () => {
    refetchProjects();
    refetchEmployees();
    refetchAssignments();
    // Clear filters on refresh
    setProjectFilter("");
    setSelectedProjectId("");
    setSelectedStaffId("");
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    setProjectFilter(projectId); // Set the filter when project is selected
  };

  const handleClearSelection = () => {
    setProjectFilter("");
    setSelectedProjectId("");
    setSelectedStaffId("");
    // Collapse all projects when clearing selection
    setExpandedProjects({});
  };

  const handleDisassignClick = (assignment: any) => {
    setAssignmentToDisassign(assignment);
    setDisassignDialogOpen(true);
  };

  const handleDisassignConfirm = () => {
    if (assignmentToDisassign) {
      disassignMutation.mutate(assignmentToDisassign.id);
    }
  };

  const handleDisassignCancel = () => {
    setDisassignDialogOpen(false);
    setAssignmentToDisassign(null);
  };

  const toggleProjectExpansion = (projectId: string) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const StatCard = ({ icon, title, value, color }: { icon: React.ReactNode, title: string, value: number, color: string }) => (
    <Card 
      sx={{ 
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
        boxShadow: `0 4px 20px 0 ${color}15`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 30px 0 ${color}25`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box p={4}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Project Allocation Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage project assignments and team allocations
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loadingProjects || loadingAssignments}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Statistics Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<BusinessCenter sx={{ fontSize: 32 }} />}
                title="Total Projects"
                value={stats.totalProjects}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<Assignment sx={{ fontSize: 32 }} />}
                title="Total Assignments"
                value={stats.totalAssignments}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<Group sx={{ fontSize: 32 }} />}
                title="Unique Staff Assigned"
                value={stats.totalUniqueStaff}
                color={theme.palette.success.main}
              />
            </Grid>
          </Grid>

          {/* Assignment Form */}
          <Card 
            sx={{ 
              mb: 4,
              borderRadius: 3,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Assign Staff to Project
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth>
                    <InputLabel>Select Project</InputLabel>
                    <Select
                      value={selectedProjectId}
                      onChange={(e) => handleProjectSelect(e.target.value)}
                      label="Select Project"
                    >
                      {projects?.map((project) => (
                        <MenuItem
                          key={project.project_id}
                          value={project.project_id}
                        >
                          {project.project_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Staff</InputLabel>
                    <Select
                      value={selectedStaffId}
                      onChange={(e) => setSelectedStaffId(e.target.value)}
                      label="Select Staff"
                    >
                      {employees?.map((user) => (
                        <MenuItem key={user.user_id} value={user.user_id}>
                          {user.fullName || user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleClearSelection}
                    startIcon={<Clear />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                    }}
                  >
                    Clear
                  </Button>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={
                      !selectedProjectId || !selectedStaffId || assignmentMutation.isLoading
                    }
                    onClick={() =>
                      assignmentMutation.mutate({
                        projectId: selectedProjectId,
                        staffId: selectedStaffId,
                      })
                    }
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                    }}
                  >
                    {assignmentMutation.isLoading ? <CircularProgress size={20} /> : "Assign Staff"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Project Assignments Section */}
          <Card 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Project Assignments
                </Typography>
                {projectFilter && (
                  <Chip 
                    label={`Filtered by: ${projects?.find(p => p.project_id === projectFilter)?.project_name}`}
                    color="primary"
                    onDelete={handleClearSelection}
                    deleteIcon={<Clear />}
                  />
                )}
              </Box>

              {Object.keys(groupedAssignments).length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                  <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {projectFilter ? 'No Assignments Found for Selected Project' : 'No Assignments Found'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {projectFilter 
                      ? 'There are no staff assigned to this project yet.' 
                      : 'Start by assigning staff to projects using the form above.'
                    }
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {Object.values(groupedAssignments).map((projectGroup: any) => (
                    <Card 
                      key={projectGroup.project_id} 
                      sx={{ 
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.light, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      }}
                    >
                      <CardHeader
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleProjectExpansion(projectGroup.project_id)}
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" color="primary.main">
                              {projectGroup.project_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Chip 
                                label={`${projectGroup.assignments.length} staff assigned`} 
                                color="primary" 
                                variant="outlined"
                              />
                              <IconButton 
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                  },
                                }}
                              >
                                {expandedProjects[projectGroup.project_id] ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Box>
                          </Box>
                        }
                      />
                      <Collapse in={expandedProjects[projectGroup.project_id]} timeout="auto" unmountOnExit>
                        <CardContent sx={{ pt: 0 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Assigned To</strong></TableCell>
                                  <TableCell><strong>Phone Number</strong></TableCell>
                                  <TableCell><strong>Assignment Date</strong></TableCell>
                                  <TableCell><strong>Assigned By</strong></TableCell>
                                  <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {projectGroup.assignments.map((assignment: any) => (
                                  <TableRow 
                                    key={assignment.id}
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <People sx={{ color: 'text.secondary', fontSize: 20 }} />
                                        {assignment.assigned_firstName} {assignment.assigned_lastName}
                                      </Box>
                                    </TableCell>
                                    <TableCell>{assignment.assigned_phone}</TableCell>
                                    <TableCell>
                                      {new Date(assignment.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      {assignment.assigned_by_firstName} {assignment.assigned_by_lastName}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        color="error"
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => handleDisassignClick(assignment)}
                                        disabled={disassignMutation.isLoading}
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2,
                                          textTransform: 'none',
                                        }}
                                      >
                                        Disassign
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Collapse>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Disassign Confirmation Dialog */}
          <Dialog
            open={disassignDialogOpen}
            onClose={handleDisassignCancel}
            aria-labelledby="disassign-dialog-title"
            PaperProps={{
              sx: { borderRadius: 3 }
            }}
          >
            <DialogTitle id="disassign-dialog-title" sx={{ pb: 1 }}>
              Confirm Disassignment
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove{" "}
                <strong>
                  {assignmentToDisassign?.assigned_firstName} {assignmentToDisassign?.assigned_lastName}
                </strong>{" "}
                from the project{" "}
                <strong>{assignmentToDisassign?.project_name}</strong>?
                <br />
                <br />
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={handleDisassignCancel} 
                disabled={disassignMutation.isLoading}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDisassignConfirm}
                color="error"
                variant="contained"
                disabled={disassignMutation.isLoading}
                startIcon={disassignMutation.isLoading ? <CircularProgress size={16} /> : <Delete />}
                sx={{ borderRadius: 2 }}
              >
                {disassignMutation.isLoading ? "Disassigning..." : "Confirm Disassign"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default HRDashboard;